// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Button, Hint, Icon, useTranslate } from '@plesk/ui-library';
import React, { useContext, useEffect, useId, useRef, useState } from 'react';
import { Locale } from 'jsw';
import type { Answer, ToolCall } from '../../../CopilotContext';
import { ToolCallContext, ToolCallDecision, toolCallKey } from './ToolCallContext';
import ToolCallGlyph from './ToolCallGlyph';
import ToolCallRow from './ToolCallRow';
import {
    BatchCounts,
    batchCounts,
    batchState,
    isAwaitingRun,
    stopsAfterRun,
    toolCallState,
} from './toolCalls';

type Translate = ReturnType<typeof useTranslate>;

const SEPARATOR = ' · ';

/**
 * `{n} tool call(s) · {clauses} · auto-approved`: the clauses count settled rows, or report
 * the progress of a run in flight or the recheck of a failed one, or are replaced by a run
 * refusal.
 */
export const batchHeadline = (
    translate: Translate,
    counts: BatchCounts,
    { inFlight, checking = false, error }: { inFlight: boolean; checking?: boolean; error?: string },
): string => {
    const locale = Locale.getSection('components.copilot');
    const parts = [locale.formatMessage('tool-call.batch.count', { count: counts.total })];
    if (error) {
        parts.push(error);
    } else if (checking) {
        parts.push(String(translate('tool-call.batch.checking')));
    } else if (inFlight) {
        parts.push(String(translate('tool-call.batch.running')));
    } else {
        const clauses = ([
            ['tool-call.batch.succeeded', counts.succeeded],
            ['tool-call.batch.failed', counts.failed],
            ['tool-call.batch.rejected', counts.rejected],
        ] as const)
            .filter(([, count]) => count > 0)
            .map(([key, count]) => locale.formatMessage(key, { count }));
        if (clauses.length) {
            parts.push(clauses.join(', '));
        }
    }
    if (counts.allAuto) {
        parts.push(String(translate('tool-call.batch.auto-approved')));
    }
    return parts.join(SEPARATOR);
};

type ToolCallBatchProps = {
    answer: Answer;
    calls: ToolCall[];
};

const ToolCallBatch = ({ answer, calls }: ToolCallBatchProps) => {
    const translate = useTranslate('components.copilot');
    const { decide, run, fetchResult, deciding, runInFlight, failedRuns, checkingRuns, latestRowId, lastAnswerKey } = useContext(ToolCallContext);
    const rowsId = useId();
    const [collapsed, setCollapsed] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const headerButtonRef = useRef<HTMLButtonElement>(null);
    // The calls the admin just decided, to move focus once their buttons are gone
    const focusAfterRef = useRef<string[] | null>(null);

    const inFlight = answer.id !== null && runInFlight.has(answer.id);
    const checking = answer.id !== null && checkingRuns.has(answer.id);
    const counts = batchCounts(calls);
    const undecided = calls.filter(call => call.decision === null);
    const batchDeciding = calls.some(call => deciding.has(toolCallKey(call)));
    const showBatchDecisions = undecided.length > 1;
    const showRun = !inFlight
        && !checking
        && !answer.runError
        && answer.id !== null
        && answer.id === latestRowId
        && isAwaitingRun(calls)
        // An all-automatic batch runs by itself; Run is its way out only once that failed
        && (!counts.allAuto || failedRuns.has(answer.id));
    // A settled batch the admin only rejected gets no reply of its own: without this, it
    // would read as a stalled assistant
    const awaitingMessage = !inFlight
        && !checking
        && answer.key === lastAnswerKey
        && calls.every(call => call.execution !== null)
        && stopsAfterRun(calls);

    const headline = batchHeadline(translate, counts, { inFlight, checking, error: answer.runError });

    const onDecide = (decisions: ToolCallDecision[]) => {
        focusAfterRef.current = decisions.map(({ toolCallId }) => toolCallId);
        decide(answer, decisions);
    };

    useEffect(() => {
        const decided = focusAfterRef.current;
        if (!decided) {
            return;
        }
        const pending = calls.filter(call => decided.includes(call.id));
        if (pending.some(call => deciding.has(toolCallKey(call)))) {
            return;
        }
        focusAfterRef.current = null;
        if (pending.some(call => call.decision === null)) {
            // The request failed and the buttons are back; focus stays where it is
            return;
        }
        const next = containerRef.current?.querySelector<HTMLElement>('[data-tool-call-action="approve"]:not([disabled])');
        (next ?? headerButtonRef.current)?.focus();
    }, [calls, deciding]);

    const toggleCollapsed = () => setCollapsed(value => !value);

    // The whole strip is the pointer target; the button inside it is the control, and the
    // action buttons handle their own clicks
    const onHeaderClick = (event: React.MouseEvent) => {
        if (!(event.target as HTMLElement).closest('button')) {
            toggleCollapsed();
        }
    };

    const decideAll = (decision: ToolCallDecision['decision']) => {
        // Only the undecided rows: re-deciding an auto-approved one fails the whole request
        onDecide(undecided.map(call => ({ toolCallId: call.id, decision })));
    };

    return (
        <>
            <div
                className="plesk-copilot-tool-batch"
                data-testid="copilot-tool-calls"
                role="region"
                aria-label={String(translate('tool-call.title'))}
                ref={containerRef}
            >
                {/* Pointer only: keyboard and assistive technology use the disclosure button inside */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div className="plesk-copilot-tool-batch__header" onClick={onHeaderClick}>
                    <button
                        type="button"
                        ref={headerButtonRef}
                        className="plesk-copilot-tool-batch__toggle"
                        aria-expanded={!collapsed}
                        aria-controls={rowsId}
                        onClick={toggleCollapsed}
                    >
                        <ToolCallGlyph state={batchState(calls, inFlight)} variant="batch" />
                        {headline}
                    </button>
                    {showBatchDecisions && (
                        <span className="plesk-copilot-tool-batch__actions">
                            <Button disabled={batchDeciding} onClick={() => decideAll('REJECTED')}>
                                {translate('tool-call.batch.reject-all')}
                            </Button>
                            <Button
                                intent="primary"
                                disabled={batchDeciding}
                                onClick={() => decideAll('APPROVED')}
                            >
                                {translate('tool-call.batch.approve-all')}
                            </Button>
                        </span>
                    )}
                    {showRun && (
                        <span className="plesk-copilot-tool-batch__actions">
                            <Button intent="primary" onClick={() => run(answer)}>
                                {translate('tool-call.batch.run')}
                            </Button>
                        </span>
                    )}
                    <span className="plesk-copilot-tool-batch__caret" aria-hidden="true">
                        <Icon name="chevron-right" intent="inactive" rotate={collapsed ? undefined : 90} />
                    </span>
                </div>
                <div className="plesk-copilot-visually-hidden" aria-live="polite" data-testid="copilot-tool-batch-live">{headline}</div>
                <ul id={rowsId} className="plesk-copilot-tool-batch__rows" hidden={collapsed}>
                    {calls.map(call => (
                        <ToolCallRow
                            key={call.id}
                            call={call}
                            state={toolCallState(call, inFlight)}
                            deciding={deciding.has(toolCallKey(call))}
                            onDecide={decision => onDecide([{ toolCallId: call.id, decision }])}
                            fetchResult={() => fetchResult(answer, call)}
                        />
                    ))}
                </ul>
            </div>
            {awaitingMessage && (
                <Hint component="p" className="plesk-copilot-tool-batch__awaiting" data-testid="copilot-tool-batch-awaiting">
                    {translate('tool-call.batch.awaiting-message')}
                </Hint>
            )}
        </>
    );
};

export default ToolCallBatch;
